// @flow
import React, { useState } from 'react'
import { applyStylingHoc, type StyleOuterProps, type StyleProps } from '@fcse/tsbob/dist/lib/styling'
import classNames from 'classnames'
import { Form, Input, Label, FormGroup } from '../../basic/Forms'
import { useIntl } from 'gatsby-plugin-intl'
import Button from '../../basic/Button'
import Checkbox from '../../basic/Checkbox'
import ResizableTextarea from './ResizableTextarea'
import FileUpload from './FileUpload'
import baseStyles from './ContactForm.scss'
import messages from './ContactForm.lang'
import { FormattedMessageSanitized } from '../../FormattedMessageSanitized'

type Props = {
  ...StyleProps,
  formClassName: string,
  btnIcon?: string,
  formHeadline?: any,
  formButtonLabel?: any
}
const ContactForm = ({ styles, btnIcon, formHeadline, formButtonLabel }: Props) => {
  const { formatMessage, locale } = useIntl()
  const [pageTitle, setPageTitle] = useState('')
  const [submitted, setSubmitted] = useState(false)
  const setHiddenTitleField = () => {
    if (global.window && global.window.document) {
      setPageTitle(`${document.title} | ${locale.toUpperCase()}`)
    }
  }
  const setTextCustomMessage = e => {
    e.target.setCustomValidity(formatMessage(messages.requiredTextInput))
  }
  const setCheckboxCustomMessage = e => {
    e.target.setCustomValidity(formatMessage(messages.requiredCheckbox))
  }
  const clearValidityMessage = e => {
    e.target.setCustomValidity('')
  }
  const validateForm = e => {
    if (global.window && global.window.document) {
      if (e.target.type && e.target.type === 'checkbox') {
        const requiredCheckBox = e.target
        if (requiredCheckBox) {
          requiredCheckBox.oninvalid = e => {
            setCheckboxCustomMessage(e)
          }
          requiredCheckBox.onchange = e => {
            clearValidityMessage(e)
          }
        }
      }
    }
    setSubmitted(true)
  }
  const submitForm = e => {
    e.preventDefault()
    e.target.submit()
  }
  return (
    <div className={classNames(styles.root, submitted && styles.isValidated)}>
      <h3 className={styles.title}>
        {formHeadline ? (
          <FormattedMessageSanitized {...formHeadline} />
        ) : (
          <FormattedMessageSanitized {...messages.contactHeadline} />
        )}
      </h3>
      <Form
        className={styles.form}
        action="https://getform.io/f/a05964e5-7cf1-4506-a728-3ea8ee244d62"
        method="POST"
        encType="multipart/form-data"
        onInvalid={validateForm}
        onSubmit={submitForm}
      >
        <FormGroup className={styles.group}>
          <Label className={styles.label}>{formatMessage(messages.labelName)}</Label>
          <Input
            type="text"
            placeholder={formatMessage(messages.placeholderName)}
            name="name"
            required
            styles={styles.input}
            onInvalid={setTextCustomMessage}
            onInput={clearValidityMessage}
          />
        </FormGroup>
        <FormGroup className={styles.group}>
          <Label className={styles.label}>{formatMessage(messages.labelCompany)}</Label>
          <Input
            type="text"
            placeholder={formatMessage(messages.placeholderCompany)}
            name="company"
            required
            styles={styles.input}
            onInvalid={setTextCustomMessage}
            onInput={clearValidityMessage}
          />
        </FormGroup>
        <FormGroup className={styles.group}>
          <Label className={styles.label}>{formatMessage(messages.labelPhone)}</Label>
          <Input type="tel" placeholder={formatMessage(messages.placeholderPhone)} name="tel" />
        </FormGroup>
        <FormGroup className={styles.group}>
          <Label className={styles.label}>{formatMessage(messages.labelMail)}</Label>
          <Input
            type="email"
            placeholder={formatMessage(messages.placeholderMail)}
            name="email"
            required
            styles={styles.input}
            onInvalid={setTextCustomMessage}
            onInput={clearValidityMessage}
          />
        </FormGroup>
        <FormGroup className={styles.group}>
          <Label className={styles.label}>{formatMessage(messages.labelFreetext)}</Label>
          <ResizableTextarea placeholder={formatMessage(messages.placeholderFreetext)} name="message" />
        </FormGroup>
        <FormGroup className={styles.group}>
          <Label className={styles.label}>{formatMessage(messages.labelFile)}</Label>
          <FileUpload name="file" />
        </FormGroup>
        <Checkbox required className={styles.checkbox} onChange={setHiddenTitleField}>
          <div
            className={styles.checkboxText}
            dangerouslySetInnerHTML= {{__html: formatMessage(messages.checkboxText)
                .replaceAll("'", '')}}
          />
        </Checkbox>
        <Input type="hidden" name="page" value={pageTitle} />
        <Button
          type="submit"
          styles={styles.btn}
          color="accent"
          iconRight={btnIcon ? btnIcon : 'icon-arrow_03-right-medium'}
        >
          {formButtonLabel || formatMessage(messages.labelButton)}
        </Button>
      </Form>
    </div>
  )
}

export default applyStylingHoc(baseStyles)<$Diff<Props, StyleProps> & StyleOuterProps, Function>(ContactForm)
